// whiteboards: private ones live with their owner, shared ones everyone draws on together.
// a board is a list of layers and a list of strokes. every change is a small op, the server applies them in order
// and sends them on, and the app applies the same ops with this same code, so both sides always agree

// every brush a stroke can be drawn with. the first ones are the originals, so old boards still read the same
export const BOARD_TOOLS = [
  "pen", "pencil", "brush", "marker", "highlighter", "eraser",
  "ink", "watercolor", "airbrush", "spray", "charcoal", "chalk", "crayon", "stipple",
  "rainbow", "glitter", "neon", "boil", "wave", "flame", "march", "glitch", "pulse",
  "smudge", "blend", "blur", "lighten", "darken", "fill"
] as const;
export type BrushTool = typeof BOARD_TOOLS[number];
// text sits on the board like a stroke so it moves, erases and undoes the same way
export type BoardTool = BrushTool | "text";
export const ALL_TOOLS: readonly string[] = [...BOARD_TOOLS, "text"];

// the retouch tools and the eraser change the pixels already on their layer instead of adding lines of their own,
// so the stroke only means something drawn after the ones beneath it, and it is never picked up or moved
export const RETOUCH_TOOLS = ["smudge", "blend", "blur", "lighten", "darken"] as const;
export function isPixelTool(tool: string): boolean {
  return tool === "eraser" || (RETOUCH_TOOLS as readonly string[]).includes(tool);
}

export const BLEND_MODES = [
  "normal", "multiply", "screen", "overlay", "soft-light", "hard-light",
  "darken", "lighten", "color-dodge", "color-burn", "difference", "exclusion"
] as const;
export type BlendMode = typeof BLEND_MODES[number];

export const TEXT_FONTS = ["sans", "serif", "mono", "display"] as const;
export type TextFont = typeof TEXT_FONTS[number];
export const MAX_TEXT = 400;

export const BOARD_PAPERS = ["#f6f2e8", "#ffffff", "#e9eef4", "#1b1c22", "#0b0d12"] as const;
export const BOARD_PATTERNS = ["none", "dots", "grid", "lines"] as const;
export type BoardPattern = typeof BOARD_PATTERNS[number];

export const MAX_LAYERS = 12;
export const MAX_FRAMES = 24;
// x, y and pressure for each point
export const MAX_POINTS = 4000;
export const MAX_STROKES = 20_000;
// an undone stroke or layer stays hidden this long so undo and redo still work, then it's dropped
export const KEEP_GONE_MS = 6 * 3600_000;

export interface BoardLayer {
  id: string;
  name: string;
  hidden: boolean;
  locked: boolean;
  opacity: number;
  mode: BlendMode;
  // who made it. on a shared board only they (and fractal) draw on it, change it or delete it
  owner: string;
  // the owner can let everyone draw here, each person still only erases their own strokes
  open: boolean;
  // the layer's animation: ids of its frames in order. empty means a plain layer with one frame. each stroke says
  // which frame it is on, strokes with no frame are on the first. whoever isn't working in the layer sees it play
  frames: string[];
  fps: number;
  // set when deleted, so the delete can be undone
  goneAt?: number;
}

export interface BoardStroke {
  id: string;
  layer: string;
  tool: BoardTool;
  color: string;
  size: number;
  opacity: number;
  // edge softness 0 to 1, how much the brush responds to pressure 0 to 1, how far apart scatter brushes stamp 0 to 1
  soft?: number;
  press?: number;
  gap?: number;
  // which of the layer's frames this is drawn on
  frame?: string;
  // text strokes carry their words and a font, size is the text size and pts is one point, its top left
  text?: string;
  font?: TextFont;
  // flat x, y, pressure triples in board units
  pts: number[];
  by: string;
  at: number;
  goneAt?: number;
}

export interface BoardMeta {
  id: string;
  name: string;
  shared: boolean;
  // who made it. private boards are only ever seen by them
  owner: string;
  paper: string;
  pattern: BoardPattern;
  createdAt: string;
  updatedAt: string;
  // who drew on it last, shown on the shelf
  editedBy: string;
  // a small png the app sends after drawing
  thumb: string;
}

export interface Board extends BoardMeta {
  // goes up by one with every batch of ops, so the app can tell when it missed some
  rev: number;
  // names for the people who own layers or drew, sent when a board opens
  people?: Record<string, string>;
  layers: BoardLayer[];
  strokes: BoardStroke[];
}

export interface BoardSummary extends BoardMeta {
  ownerName: string;
  editedByName: string;
  strokes: number;
}

export type Matrix = [number, number, number, number, number, number];

export type BoardOp =
  | { type: "stroke"; stroke: BoardStroke }
  | { type: "erase"; ids: string[] }
  | { type: "restore"; ids: string[] }
  | { type: "layer-add"; layer: BoardLayer; index: number }
  | { type: "layer-set"; id: string; patch: Partial<Pick<BoardLayer, "name" | "hidden" | "locked" | "opacity" | "mode" | "open" | "frames" | "fps">> }
  // moves, scales and turns strokes. m is a 2d matrix a, b, c, d, e, f like canvas setTransform
  | { type: "xform"; ids: string[]; m: Matrix }
  | { type: "layer-move"; id: string; index: number }
  | { type: "layer-remove"; id: string }
  | { type: "layer-restore"; id: string }
  | { type: "board-set"; patch: Partial<Pick<BoardMeta, "name" | "paper" | "pattern">> };

// what the server sends every open screen after a batch
export interface BoardOpsEvent {
  board: string;
  shared: boolean;
  // rev before and after, a screen whose rev isn't `from` reloads the board instead
  from: number;
  rev: number;
  ops: BoardOp[];
  by: string;
  // so a screen can label a layer's owner it has not met yet
  name?: string;
}

// where someone is on a shared board and whether their pen is down. never saved, only passed along
export interface BoardPresence {
  board: string;
  session: string;
  by: string;
  name: string;
  x: number;
  y: number;
  // pen down
  down: boolean;
  color: string;
  size: number;
  // true when they have left the board
  gone?: boolean;
}

export interface BoardsUpdatedEvent {
  shared: boolean;
  // a deleted board closes on screens that have it open
  removed?: string;
}

const ID = /^[a-z0-9_-]{1,40}$/i;
const COLOR = /^#[0-9a-f]{6}$/i;

function num(value: unknown, min: number, max: number, fallback: number): number {
  const given = Number(value);
  return Number.isFinite(given) ? Math.min(max, Math.max(min, given)) : fallback;
}

function cleanId(value: unknown): string {
  const text = String(value || "");
  return ID.test(text) ? text : "";
}

export function cleanName(value: unknown, fallback: string): string {
  return String(value || "").replace(/\s+/g, " ").trim().slice(0, 60) || fallback;
}

function cleanColor(value: unknown, fallback = "#1d1c22"): string {
  const text = String(value || "");
  return COLOR.test(text) ? text.toLowerCase() : fallback;
}

export function cleanPaper(value: unknown): string {
  return cleanColor(value, BOARD_PAPERS[0]);
}

export function cleanPattern(value: unknown): BoardPattern {
  return (BOARD_PATTERNS as readonly string[]).includes(String(value)) ? value as BoardPattern : "none";
}

export function cleanFrames(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const ids = [...new Set(value.map((item) => (item === "" ? "" : cleanId(item))))].slice(0, MAX_FRAMES);
  // just the unnamed first frame is a plain layer
  return ids.length === 1 && ids[0] === "" ? [] : ids;
}

// the frames a layer plays, a plain layer has one with no name
export function layerTimeline(layer: Pick<BoardLayer, "frames">): string[] {
  return layer.frames.length ? layer.frames : [""];
}

export function cleanMode(value: unknown): BlendMode {
  return (BLEND_MODES as readonly string[]).includes(String(value)) ? value as BlendMode : "normal";
}

export function cleanLayer(raw: Partial<BoardLayer>, fallbackName = "layer"): BoardLayer | null {
  const id = cleanId(raw?.id);
  if (!id) return null;
  const owner = cleanId(raw.owner);
  return {
    id,
    name: cleanName(raw.name, fallbackName),
    hidden: Boolean(raw.hidden),
    locked: Boolean(raw.locked),
    opacity: num(raw.opacity, 0, 1, 1),
    mode: cleanMode(raw.mode),
    owner,
    // layers from before ownership had no owner and stay open to everyone
    open: raw.open === undefined ? !owner : Boolean(raw.open),
    frames: cleanFrames(raw.frames),
    fps: Math.round(num(raw.fps, 1, 24, 8)),
    ...(raw.goneAt ? { goneAt: num(raw.goneAt, 0, 9e15, 0) } : {})
  };
}

// strokes come from the app, so every number is checked and rounded to keep files small
export function cleanStroke(raw: Partial<BoardStroke>, by: string): BoardStroke | null {
  const id = cleanId(raw?.id);
  const layer = cleanId(raw?.layer);
  if (!id || !layer || !Array.isArray(raw.pts)) return null;
  const tool = ALL_TOOLS.includes(String(raw.tool)) ? raw.tool as BoardTool : "pen";
  const pts: number[] = [];
  const given = raw.pts.slice(0, (tool === "text" ? 1 : MAX_POINTS) * 3);
  for (let at = 0; at + 2 < given.length; at += 3) {
    const x = Number(given[at]);
    const y = Number(given[at + 1]);
    if (!Number.isFinite(x) || !Number.isFinite(y) || Math.abs(x) > 1e6 || Math.abs(y) > 1e6) continue;
    pts.push(Math.round(x * 10) / 10, Math.round(y * 10) / 10, Math.round(num(given[at + 2], 0, 1, 0.5) * 100) / 100);
  }
  if (!pts.length) return null;
  const stroke: BoardStroke = {
    id,
    layer,
    tool,
    color: cleanColor(raw.color),
    size: Math.round(num(raw.size, 0.5, 2000, 4) * 10) / 10,
    opacity: Math.round(num(raw.opacity, 0.02, 1, 1) * 100) / 100,
    pts,
    by,
    at: num(raw.at, 0, 9e15, Date.now()),
    ...(raw.goneAt ? { goneAt: num(raw.goneAt, 0, 9e15, 0) } : {})
  };
  if (raw.soft !== undefined) stroke.soft = Math.round(num(raw.soft, 0, 1, 0) * 100) / 100;
  if (raw.press !== undefined) stroke.press = Math.round(num(raw.press, 0, 1, 1) * 100) / 100;
  const frame = cleanId(raw.frame);
  if (frame) stroke.frame = frame;
  if (raw.gap !== undefined) stroke.gap = Math.round(num(raw.gap, 0, 1, 0.3) * 100) / 100;
  if (tool === "text") {
    const text = String(raw.text || "").replace(/\r/g, "").replace(/\n{3,}/g, "\n\n").slice(0, MAX_TEXT);
    if (!text.trim()) return null;
    stroke.text = text;
    stroke.font = (TEXT_FONTS as readonly string[]).includes(String(raw.font)) ? raw.font as TextFont : "sans";
    stroke.pts = pts.slice(0, 3);
  }
  return stroke;
}

export function cleanMatrix(value: unknown): Matrix | null {
  if (!Array.isArray(value) || value.length !== 6) return null;
  const m = value.map((item) => Number(item));
  if (m.some((item) => !Number.isFinite(item))) return null;
  const det = m[0] * m[3] - m[1] * m[2];
  if (Math.abs(det) < 1e-4 || Math.abs(det) > 1e4 || Math.abs(m[4]) > 1e6 || Math.abs(m[5]) > 1e6) return null;
  return m.map((item) => Math.round(item * 1e6) / 1e6) as Matrix;
}

export function invertMatrix(m: Matrix): Matrix {
  const det = m[0] * m[3] - m[1] * m[2];
  const a = m[3] / det;
  const b = -m[1] / det;
  const c = -m[2] / det;
  const d = m[0] / det;
  return [a, b, c, d, -(a * m[4] + c * m[5]), -(b * m[4] + d * m[5])];
}

function cleanIds(value: unknown): string[] {
  return Array.isArray(value) ? [...new Set(value.map(cleanId).filter(Boolean))].slice(0, MAX_STROKES) : [];
}

// turns whatever the app sent into ops this code knows, dropping anything odd
export function cleanOps(raw: unknown, by: string): BoardOp[] {
  if (!Array.isArray(raw)) return [];
  const ops: BoardOp[] = [];
  for (const item of raw.slice(0, 200)) {
    const op = item as Record<string, unknown>;
    switch (op?.type) {
      case "stroke": {
        const stroke = cleanStroke(op.stroke as Partial<BoardStroke>, by);
        if (stroke) ops.push({ type: "stroke", stroke: { ...stroke, goneAt: undefined } });
        break;
      }
      case "erase":
      case "restore": {
        const ids = cleanIds(op.ids);
        if (ids.length) ops.push({ type: op.type, ids });
        break;
      }
      case "layer-add": {
        const layer = cleanLayer({ ...(op.layer as Partial<BoardLayer>), owner: by, open: Boolean((op.layer as Partial<BoardLayer>)?.open) });
        if (layer) ops.push({ type: "layer-add", layer: { ...layer, goneAt: undefined }, index: Math.floor(num(op.index, 0, MAX_LAYERS, MAX_LAYERS)) });
        break;
      }
      case "layer-set": {
        const id = cleanId(op.id);
        const given = (op.patch || {}) as Record<string, unknown>;
        const patch: Extract<BoardOp, { type: "layer-set" }>["patch"] = {};
        if (given.name !== undefined) patch.name = cleanName(given.name, "layer");
        if (given.hidden !== undefined) patch.hidden = Boolean(given.hidden);
        if (given.locked !== undefined) patch.locked = Boolean(given.locked);
        if (given.opacity !== undefined) patch.opacity = num(given.opacity, 0, 1, 1);
        if (given.mode !== undefined) patch.mode = cleanMode(given.mode);
        if (given.open !== undefined) patch.open = Boolean(given.open);
        if (given.frames !== undefined) patch.frames = cleanFrames(given.frames);
        if (given.fps !== undefined) patch.fps = Math.round(num(given.fps, 1, 24, 8));
        if (id && Object.keys(patch).length) ops.push({ type: "layer-set", id, patch });
        break;
      }
      case "xform": {
        const ids = cleanIds(op.ids);
        const m = cleanMatrix(op.m);
        if (ids.length && m) ops.push({ type: "xform", ids, m });
        break;
      }
      case "layer-move": {
        const id = cleanId(op.id);
        if (id) ops.push({ type: "layer-move", id, index: Math.floor(num(op.index, 0, MAX_LAYERS, 0)) });
        break;
      }
      case "layer-remove":
      case "layer-restore": {
        const id = cleanId(op.id);
        if (id) ops.push({ type: op.type, id });
        break;
      }
      case "board-set": {
        const given = (op.patch || {}) as Record<string, unknown>;
        const patch: Extract<BoardOp, { type: "board-set" }>["patch"] = {};
        if (given.name !== undefined) patch.name = cleanName(given.name, "board");
        if (given.paper !== undefined) patch.paper = cleanPaper(given.paper);
        if (given.pattern !== undefined) patch.pattern = cleanPattern(given.pattern);
        if (Object.keys(patch).length) ops.push({ type: "board-set", patch });
        break;
      }
      default:
        break;
    }
  }
  return ops;
}

export function liveLayers(board: Pick<Board, "layers">): BoardLayer[] {
  return board.layers.filter((layer) => !layer.goneAt);
}

// applies ops in place. every op is safe to apply twice, so the app can apply its own ops straight away
// and again when the server echoes them back in the server's order. returns the layer ids that changed
export function applyOps(board: Board, ops: BoardOp[], now = Date.now()): Set<string> {
  const touched = new Set<string>();
  const strokeAt = new Map(board.strokes.map((stroke, index) => [stroke.id, index]));
  let reindex = false;
  const find = (id: string): BoardStroke | undefined => {
    if (reindex) {
      strokeAt.clear();
      board.strokes.forEach((stroke, index) => strokeAt.set(stroke.id, index));
      reindex = false;
    }
    const at = strokeAt.get(id);
    return at === undefined ? undefined : board.strokes[at];
  };
  for (const op of ops) {
    switch (op.type) {
      case "stroke": {
        if (!board.layers.some((layer) => layer.id === op.stroke.layer)) break;
        // an echo of a stroke already drawn moves to where the server put it
        if (find(op.stroke.id)) {
          board.strokes = board.strokes.filter((stroke) => stroke.id !== op.stroke.id);
        }
        board.strokes.push({ ...op.stroke });
        reindex = true;
        touched.add(op.stroke.layer);
        break;
      }
      case "erase":
      case "restore":
        for (const id of op.ids) {
          const stroke = find(id);
          if (!stroke) continue;
          if (op.type === "erase" && !stroke.goneAt) stroke.goneAt = now;
          if (op.type === "restore") delete stroke.goneAt;
          touched.add(stroke.layer);
        }
        break;
      case "xform": {
        for (const id of op.ids) {
          const at = strokeAt.get(id);
          const stroke = at === undefined ? undefined : board.strokes[at];
          if (!stroke || at === undefined) continue;
          // a new object, so anything cached against the old stroke is rebuilt
          board.strokes[at] = moveStroke(stroke, op.m);
          touched.add(stroke.layer);
        }
        break;
      }
      case "layer-add": {
        if (board.layers.some((layer) => layer.id === op.layer.id)) break;
        if (liveLayers(board).length >= MAX_LAYERS) break;
        board.layers.splice(Math.min(op.index, board.layers.length), 0, { ...op.layer });
        touched.add(op.layer.id);
        break;
      }
      case "layer-set": {
        const layer = board.layers.find((item) => item.id === op.id);
        if (!layer) break;
        Object.assign(layer, op.patch);
        touched.add(layer.id);
        break;
      }
      case "layer-move": {
        const from = board.layers.findIndex((item) => item.id === op.id);
        if (from < 0) break;
        const [layer] = board.layers.splice(from, 1);
        board.layers.splice(Math.min(op.index, board.layers.length), 0, layer);
        touched.add(layer.id);
        break;
      }
      case "layer-remove":
      case "layer-restore": {
        const layer = board.layers.find((item) => item.id === op.id);
        if (!layer) break;
        // the last layer always stays
        if (op.type === "layer-remove" && !layer.goneAt && liveLayers(board).length > 1) layer.goneAt = now;
        if (op.type === "layer-restore") delete layer.goneAt;
        touched.add(layer.id);
        break;
      }
      case "board-set":
        Object.assign(board, op.patch);
        touched.add("*");
        break;
    }
  }
  return touched;
}

// drops strokes and layers that were undone long ago, and the oldest strokes if a board ever gets huge
export function compactBoard(board: Board, now = Date.now()): void {
  const dropLayers = new Set(board.layers.filter((layer) => layer.goneAt && now - layer.goneAt > KEEP_GONE_MS).map((layer) => layer.id));
  board.layers = board.layers.filter((layer) => !dropLayers.has(layer.id));
  const timelines = new Map(board.layers.map((layer) => [layer.id, new Set(layerTimeline(layer))]));
  // a deleted frame keeps its strokes until the board is next opened, so undo can bring it back
  board.strokes = board.strokes.filter((stroke) => !dropLayers.has(stroke.layer) && !(stroke.goneAt && now - stroke.goneAt > KEEP_GONE_MS)
    && (timelines.get(stroke.layer)?.has(stroke.frame || "") ?? true));
  if (board.strokes.length > MAX_STROKES) board.strokes = board.strokes.slice(board.strokes.length - MAX_STROKES);
}

export function newLayer(id: string, name: string, owner = "", open = false): BoardLayer {
  return { id, name, hidden: false, locked: false, opacity: 1, mode: "normal", owner, open, frames: [], fps: 8 };
}

// a stroke after a matrix. text keeps upright, it only moves and grows
export function moveStroke(stroke: BoardStroke, m: Matrix): BoardStroke {
  const pts = stroke.pts.slice();
  for (let at = 0; at + 2 < pts.length; at += 3) {
    const x = pts[at];
    const y = pts[at + 1];
    pts[at] = Math.round((m[0] * x + m[2] * y + m[4]) * 10) / 10;
    pts[at + 1] = Math.round((m[1] * x + m[3] * y + m[5]) * 10) / 10;
  }
  const grow = Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2]));
  return { ...stroke, pts, size: Math.min(2000, Math.max(0.5, Math.round(stroke.size * grow * 10) / 10)) };
}

// --- who may do what on a shared board

export interface Actor {
  id: string;
  // fractal, who can step in on anything
  admin: boolean;
}

export function canDrawOn(board: Pick<Board, "shared">, layer: BoardLayer | undefined, who: Actor): boolean {
  if (!layer) return false;
  if (!board.shared) return true;
  return who.admin || layer.open || layer.owner === who.id;
}

export function canEditLayer(board: Pick<Board, "shared">, layer: BoardLayer | undefined, who: Actor): boolean {
  if (!layer) return false;
  if (!board.shared) return true;
  return who.admin || layer.owner === who.id;
}

export function canEditStroke(board: Pick<Board, "shared">, stroke: BoardStroke | undefined, who: Actor): boolean {
  if (!stroke) return false;
  if (!board.shared) return true;
  return who.admin || stroke.by === who.id;
}

// the pixel eraser rubs out everything under it on its layer, so it only works where it can't hit anyone else's lines
export function canRub(board: Pick<Board, "shared" | "strokes">, layerId: string, who: Actor): boolean {
  if (!board.shared || who.admin) return true;
  return !board.strokes.some((stroke) => stroke.layer === layerId && !stroke.goneAt && !isPixelTool(stroke.tool) && stroke.by !== who.id);
}

// the ops this person may make. the server uses it on what arrives, the app on what it is about to send
export function permitOps(board: Board, ops: BoardOp[], who: Actor): BoardOp[] {
  if (!board.shared || who.admin) return ops;
  const layers = new Map(board.layers.map((layer) => [layer.id, { ...layer }]));
  const strokes = new Map(board.strokes.map((stroke) => [stroke.id, stroke.by]));
  const own = (id: string) => strokes.get(id) === who.id;
  const out: BoardOp[] = [];
  for (const op of ops) {
    switch (op.type) {
      case "stroke": {
        const layer = layers.get(op.stroke.layer);
        if (!layer || !(layer.open || layer.owner === who.id)) break;
        if (isPixelTool(op.stroke.tool) && board.strokes.some((item) => item.layer === layer.id && !item.goneAt && !isPixelTool(item.tool) && item.by !== who.id)) break;
        strokes.set(op.stroke.id, who.id);
        out.push(op);
        break;
      }
      case "erase":
      case "restore":
      case "xform": {
        const ids = op.ids.filter(own);
        if (ids.length) out.push({ ...op, ids } as BoardOp);
        break;
      }
      case "layer-add":
        layers.set(op.layer.id, { ...op.layer, owner: who.id });
        out.push(op);
        break;
      case "layer-set":
      case "layer-move":
      case "layer-remove":
      case "layer-restore":
        if (layers.get(op.id)?.owner === who.id) out.push(op);
        break;
      default:
        out.push(op);
    }
  }
  return out;
}

// the same colour for the same person on every screen
export function userHue(id: string): number {
  let hash = 7;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) % 360;
  return hash;
}

export function userColor(id: string): string {
  return `hsl(${userHue(id)} 78% 62%)`;
}
